import { useCallback, useRef, useState } from 'react';

export const useAudioRecorder = (
  onAudioData: (audioBlob: Blob) => void,
  onRecordingStateChange: (isRecording: boolean) => void,
  onError: (error: string) => void,
  maxDuration: number = 30
) => {
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const [isRecording, setIsRecording] = useState(false);
  const stopRecording = useCallback(async () => {
    return new Promise<void>((resolve) => {
      if (mediaRecorderRef.current && isRecording) {
        const handleStop = () => {
          mediaRecorderRef.current?.removeEventListener('stop', handleStop);
          resolve();
        };
        mediaRecorderRef.current.addEventListener('stop', handleStop);
        mediaRecorderRef.current.stop();
      } else {
        resolve();
      }
      
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
        streamRef.current = null;
      }
        setIsRecording(false);
      onRecordingStateChange(false);
    });
  }, [isRecording, onRecordingStateChange]);

  const startRecording = useCallback(async () => {
    try {
      // Request microphone access
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          sampleRate: 16000,
        },
      });

      streamRef.current = stream;
      chunksRef.current = [];

      // Create MediaRecorder
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: 'audio/webm;codecs=opus',
      });

      mediaRecorderRef.current = mediaRecorder;

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        if (chunksRef.current.length > 0) {
          const audioBlob = new Blob(chunksRef.current, { type: 'audio/webm;codecs=opus' });
          onAudioData(audioBlob);
        }
        chunksRef.current = [];
      };

      mediaRecorder.onerror = (event) => {
        console.error('MediaRecorder error:', event);
        onError('Recording failed');
        stopRecording();
      };

      // Start recording
      mediaRecorder.start();
      setIsRecording(true);
      onRecordingStateChange(true);

      // Set maximum recording duration
      timeoutRef.current = setTimeout(() => {
        onError(`Recording stopped: maximum duration of ${maxDuration} seconds reached`);
        stopRecording();
      }, maxDuration * 1000);

    } catch (error) {
      console.error('Failed to start recording:', error);
      
      if (error instanceof Error) {
        if (error.name === 'NotAllowedError') {
          onError('Microphone access denied. Please allow microphone permissions.');
        } else if (error.name === 'NotFoundError') {
          onError('No microphone found. Please connect a microphone.');
        } else {
          onError(`Failed to start recording: ${error.message}`);
        }
      } else {
        onError('Failed to start recording');
      }
    }
  }, [onAudioData, onRecordingStateChange, onError, maxDuration, stopRecording]);

  // Cleanup on unmount
  const cleanup = useCallback(() => {
    stopRecording();
  }, [stopRecording]);

  return {
    startRecording,
    stopRecording,
    isRecording,
    cleanup,
  };
};
